import { action, makeObservable, observable, flow } from "mobx";
import { 
  getToolsList, 
  getActionsList, 
  getAgentTools, 
  updateAgentTools,
  addToolToAgent,
  removeToolFromAgent
} from "../../services";

class ToolsStore {
  // Loading states
  isLoadingToolsList = false;
  isLoadingActionsList = false;
  isLoadingAgentTools = false;
  isUpdatingTools = false;

  // Error states
  toolsListError = '';
  actionsListError = '';
  agentToolsError = '';
  updateToolsError = '';

  // Data
  availableTools = []; 
  availableActions = []; 
  installedTools = []; 

  // Filters
  toolsListFilters = {
    type: null,
    name: null,
    tags: null,
    category: null
  };

  actionsListFilters = {
    type: null,
    name: null,
    category: null
  };

  // Current agent
  currentAgentUuid = null;

  // Alert/Notification state
  showAlert = false;
  alertMessage = '';
  alertType = 'success';

  constructor() {
    makeObservable(this, {
      // Loading states
      isLoadingToolsList: observable,
      isLoadingActionsList: observable,
      isLoadingAgentTools: observable,
      isUpdatingTools: observable,

      // Error states
      toolsListError: observable,
      actionsListError: observable,
      agentToolsError: observable,
      updateToolsError: observable,

      // Data
      availableTools: observable,
      availableActions: observable,
      installedTools: observable,

      // Filters
      toolsListFilters: observable,
      actionsListFilters: observable,

      // Current agent
      currentAgentUuid: observable,

      // Alert/Notification
      showAlert: observable,
      alertMessage: observable,
      alertType: observable,

      // Actions
      setToolsListFilters: action,
      setActionsListFilters: action,
      setCurrentAgentUuid: action,
      setInstalledTools: action,
      clearToolsListFilters: action,
      clearActionsListFilters: action,
      showNotification: action,
      hideNotification: action,
      clearErrors: action,

      // Flows
      fetchToolsList: flow,
      fetchActionsList: flow,
      fetchAgentTools: flow,
      addTools: flow,
      removeTool: flow,
      toggleTool: flow,
      updateToolSettings: flow,
    });
  }

  // Actions
  setToolsListFilters(filters) {
    this.toolsListFilters = { ...this.toolsListFilters, ...filters };
  }

  setActionsListFilters(filters) {
    this.actionsListFilters = { ...this.actionsListFilters, ...filters };
  }

  clearToolsListFilters() {
    this.toolsListFilters = {
      type: null,
      name: null,
      tags: null,
      category: null
    };
  }

  clearActionsListFilters() {
    this.actionsListFilters = {
      type: null,
      name: null,
      category: null
    };
  }

  setCurrentAgentUuid(agentUuid) {
    this.currentAgentUuid = agentUuid;
  }

  setInstalledTools(tools) {
    this.installedTools = tools;
  }

  showNotification(message, type = 'success') {
    this.alertMessage = message;
    this.alertType = type;
    this.showAlert = true;
  }

  hideNotification() {
    this.showAlert = false;
    this.alertMessage = '';
  }

  clearErrors() {
    this.toolsListError = '';
    this.actionsListError = '';
    this.agentToolsError = '';
    this.updateToolsError = '';
  }

  // Flows
  *fetchToolsList(filters = {}) {
    try {
      this.isLoadingToolsList = true;
      this.toolsListError = '';

      const mergedFilters = { ...this.toolsListFilters, ...filters };
      const response = yield getToolsList(mergedFilters);
      this.availableTools = response.data?.data || [];

      return this.availableTools;
    } catch (error) {
      console.error('Error fetching tools list:', error);
      this.toolsListError = 'Failed to load tools list. Please try again.';
      this.availableTools = [];
      throw error;
    } finally {
      this.isLoadingToolsList = false;
    }
  }

  *fetchActionsList(filters = {}) {
    try {
      this.isLoadingActionsList = true;
      this.actionsListError = '';

      const mergedFilters = { ...this.actionsListFilters, ...filters };
      const response = yield getActionsList(mergedFilters);
      this.availableActions = response.data?.data || [];

      return this.availableActions;
    } catch (error) {
      console.error('Error fetching actions list:', error);
      this.actionsListError = 'Failed to load actions list. Please try again.';
      this.availableActions = [];
      throw error;
    } finally {
      this.isLoadingActionsList = false;
    }
  }

  *fetchAgentTools(agentUuid) {
    try {
      this.isLoadingAgentTools = true;
      this.agentToolsError = '';
      this.setCurrentAgentUuid(agentUuid);

      const response = yield getAgentTools(agentUuid);
      const tools = response.data?.data || [];
      this.setInstalledTools(Array.isArray(tools) ? tools : []);

      return this.installedTools;
    } catch (error) {
      console.error('Error fetching agent tools:', error);
      this.agentToolsError = 'Failed to load agent tools. Please try again.';
      this.setInstalledTools([]);
      throw error;
    } finally {
      this.isLoadingAgentTools = false;
    }
  }

  *addTools(agentUuid, newTools) {
    if (!agentUuid || !newTools?.length) return;

    try {
      this.isUpdatingTools = true;
      this.updateToolsError = '';

      const toolsToAdd = newTools.map((tool) => ({
        id: tool.id || tool.action_uuid || tool.tool_id,
        title: tool.title || tool.name,
        description: tool.description || '',
        icon: tool.icon || '',
        isEnabled: true,
        tag: tool.tag || 'Ready to use',
        tagColor: tool.tagColor || 'informative',
        settings: tool.settings || {},
        type: tool.type || '',
        category: tool.category || tool.categories || ''
      }));

      // Optimistic update
      const updatedTools = [...this.installedTools, ...toolsToAdd];
      this.setInstalledTools(updatedTools);

      // Llamar al servicio
      yield updateAgentTools(agentUuid, { tools: updatedTools });

      this.showNotification('Tools added successfully', 'success');
      return toolsToAdd;
    } catch (error) {
      console.error('Error adding tools:', error);
      this.updateToolsError = 'Failed to add tools. Please try again.';
      // Revertir cambios
      yield this.fetchAgentTools(agentUuid);
      throw error;
    } finally {
      this.isUpdatingTools = false;
    }
  }

  *removeTool(agentUuid, toolId) {
    if (!agentUuid) return;

    try {
      this.isUpdatingTools = true;
      this.updateToolsError = '';

      // Optimistic update
      const updatedTools = this.installedTools.filter(tool => tool.id !== toolId);
      this.setInstalledTools(updatedTools);

      // Llamar al servicio
      yield removeToolFromAgent(agentUuid, toolId);

      this.showNotification('Tool removed successfully', 'success');
    } catch (error) {
      console.error('Error removing tool:', error);
      this.updateToolsError = 'Failed to remove tool. Please try again.';
      // Revertir cambios
      yield this.fetchAgentTools(agentUuid);
      throw error;
    } finally {
      this.isUpdatingTools = false;
    }
  }

  *toggleTool(agentUuid, toolId) {
    if (!agentUuid) return;

    try {
      this.isUpdatingTools = true;
      this.updateToolsError = '';

      // Optimistic update
      const updatedTools = this.installedTools.map(tool =>
        tool.id === toolId ? { ...tool, isEnabled: !tool.isEnabled } : tool
      );
      this.setInstalledTools(updatedTools);

      // Llamar al servicio
      yield updateAgentTools(agentUuid, { tools: updatedTools });

      this.showNotification('Tool updated successfully', 'success');
    } catch (error) {
      console.error('Error toggling tool:', error);
      this.updateToolsError = 'Failed to update tool. Please try again.';
      // Revertir cambios
      yield this.fetchAgentTools(agentUuid);
      throw error;
    } finally {
      this.isUpdatingTools = false;
    }
  }

  *updateToolSettings(agentUuid, toolId, settings) {
    if (!agentUuid) return;

    try {
      this.isUpdatingTools = true;
      this.updateToolsError = '';

      // Optimistic update
      const updatedTools = this.installedTools.map(tool =>
        tool.id === toolId
          ? { ...tool, settings: { ...tool.settings, ...settings } }
          : tool
      );
      this.setInstalledTools(updatedTools);

      // Llamar al servicio
      yield updateAgentTools(agentUuid, { tools: updatedTools });

      this.showNotification('Tool configuration saved successfully', 'success');
    } catch (error) {
      console.error('Error updating tool settings:', error);
      this.updateToolsError = 'Failed to update tool configuration. Please try again.';
      // Revertir cambios
      yield this.fetchAgentTools(agentUuid);
      throw error;
    } finally {
      this.isUpdatingTools = false;
    }
  }

  reset() {
    this.isLoadingToolsList = false;
    this.isLoadingActionsList = false;
    this.isLoadingAgentTools = false;
    this.isUpdatingTools = false;

    this.clearErrors();

    this.availableTools = [];
    this.availableActions = [];
    this.installedTools = [];

    this.clearToolsListFilters();
    this.clearActionsListFilters();

    this.currentAgentUuid = null;

    this.showAlert = false;
    this.alertMessage = '';
    this.alertType = 'success';
  }
}

export default ToolsStore;

